import { useFeature } from "@growthbook/growthbook-react";
import { useQueries } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { createContext, useCallback, useEffect, useRef, useState } from "react";

import { useBudgetPlanQueryOptions } from "@features/budgeting-game/api";
import { useSelectedCareersQueryOptions } from "@features/career-compare/api/use-selected-careers";
import { useCareerPlanQueryOptions } from "@features/career-details/api/use-career-plan";
import { isNullish } from "@lib/app.helpers";
import { useJourneyStepsQueryOptions } from "@pages/journey-map/api/use-journey-steps";
import { useUpdateUserJourney } from "@pages/journey-map/api/use-update-user-journey";
import { useUserJourneyQueryOptions } from "@pages/journey-map/api/use-user-journey";
import { useUserJourneyStatusQueryOptions } from "@pages/journey-map/api/use-user-journey-status";

export const JourneyContext = createContext();

export const STEP_TO_MAP = {
  BEGIN: 1,
  INT_INV: 1,
  MATCHES: 2,
  COMPARE: 2,
  BUDGET: 2,
  HIGH_SCH: 3,
  SKILLS: 3,
  POST_HIGH_SCH: 3,
  GOALS: 4,
  SHARE: 4,
  DONE: 4,
};

export const JOURNEY_MAP_VIEW_ERRORED = "errored";

export const MAP_NUMBER_TO_NAME = {
  1: "Get Ready",
  2: "Career Matches",
  3: "Career Pathway",
  4: "Goals",
};

const pass = () => true;

const hasData =
  (key, next = pass) =>
  (data) => {
    const keys = key.split(".");

    let obj = data;
    for (const key of keys) {
      obj = obj[key];
      if (!obj) {
        return false;
      }
    }

    return next(data);
  };

const customValidator =
  (validator, next = pass) =>
  (data) => {
    if (!validator(data)) {
      return false;
    }

    return next(data);
  };

const MAP_UNLOCK_REQUIREMENTS = [
  // Map 0 -- this map doesn't exist
  pass,
  // Map 1
  pass,
  // Map 2
  hasData("userJourney.surveyCompletedAt"),
  // Map 3
  hasData("userJourney.currentJourneyCareer"),
  // Map 4
  pass,
  // Loop back
  pass,
];

const STEP_UNLOCK_REQUIREMENTS = {
  MATCHES: hasData("userJourney.surveyCompletedAt"),
  COMPARE: customValidator(
    // select 3 careers or unlock compare for the first time
    (data) =>
      data.selectedCareers?.length > 0 || !!data.userJourneyStatus?.compare,
  ),
  BUDGET: hasData("userJourney.currentJourneyCareer"),
  HIGH_SCH: hasData("userJourney.currentJourneyCareer"),
  SKILLS: hasData("userJourney.currentJourneyCareer"),
  POST_HIGH_SCH: hasData("userJourney.currentJourneyCareer"),
  GOALS: hasData("userJourney.currentJourneyCareer"),
  SHARE: hasData("careerPlan.submittedAt"),
};

export const JourneyProvider = ({ children }) => {
  const userJourneyLoaded = useRef(false);
  // Stays true after first full load; prevents isJourneyLoading from flipping
  // back to true when the budget-plan query key changes (e.g. career selected).
  const journeyEverLoaded = useRef(false);

  const [journeyMapView, setJourneyMapView] = useState(true);
  const [currentMap, setCurrentMap] = useState();
  const [unlockedMaps, setUnlockedMaps] = useState([]);
  const [unlockedSteps, setUnlockedSteps] = useState([]);

  const journeyMapViewFeature = useFeature("trek-mobile.journey-map-view");

  useEffect(() => {
    if (!isNullish(journeyMapViewFeature.value)) {
      setJourneyMapView(!journeyMapViewFeature.value);
    }
  }, [journeyMapViewFeature.value]);

  const { mutateAsync: updateUserJourney } = useUpdateUserJourney();

  const results = useQueries({
    queries: [
      useJourneyStepsQueryOptions(),
      useUserJourneyQueryOptions(),
      useCareerPlanQueryOptions(),
      useSelectedCareersQueryOptions(),
      useUserJourneyStatusQueryOptions(),
      useBudgetPlanQueryOptions(),
    ],
  });
  const [
    journeySteps,
    userJourney,
    careerPlan,
    selectedCareers,
    userJourneyStatus,
    budgetPlan,
  ] = results.map((r) => r.data);

  const isFetched = results.reduce((acc, r) => acc && r.isFetched, true);
  if (isFetched) {
    journeyEverLoaded.current = true;
  }

  const requestMapChange = useCallback(
    (requestedMap) => {
      const firstLockedMap = unlockedMaps.findIndex((unlocked) => !unlocked);

      let newMap = requestedMap;
      if (firstLockedMap !== -1) {
        newMap = Math.min(firstLockedMap - 1, newMap);
      }
      setCurrentMap(newMap);
    },
    [unlockedMaps],
  );

  const requestStepChange = useCallback(
    async (journeyStep) => {
      const stepChanged =
        journeyStep.stepNum !== userJourney.currentJourneyStep.stepNum;

      if (stepChanged && unlockedSteps[journeyStep.stepNum]) {
        await updateUserJourney({
          id: userJourney.id,
          stepNum: journeyStep.stepNum,
        });

        return true;
      }

      return !stepChanged;
    },
    [unlockedSteps, userJourney],
  );

  useEffect(() => {
    // Don't bother updating unlocked maps until all data has loaded.
    if (isFetched) {
      const unlockData = {
        journeySteps,
        userJourney,
        careerPlan,
        selectedCareers,
        userJourneyStatus,
        budgetPlan,
      };
      const unlockedMaps = MAP_UNLOCK_REQUIREMENTS.map((isUnlocked) =>
        isUnlocked(unlockData),
      );
      setUnlockedMaps(unlockedMaps);

      const unlockedSteps = journeySteps.steps.map(
        (step) =>
          !Object.hasOwn(STEP_UNLOCK_REQUIREMENTS, step.code) ||
          STEP_UNLOCK_REQUIREMENTS[step.code](unlockData),
      );
      setUnlockedSteps(unlockedSteps);
    }
  }, [
    isFetched,
    journeySteps,
    userJourney,
    careerPlan,
    selectedCareers,
    userJourneyStatus,
    budgetPlan,
  ]);

  useEffect(() => {
    // Find the map that the current journey step lives on.
    if (unlockedMaps.length > 0 && !userJourneyLoaded.current) {
      userJourneyLoaded.current = true;
      const stepMap = STEP_TO_MAP[userJourney.currentJourneyStep.code];
      requestMapChange(stepMap);
    }
  }, [unlockedMaps, userJourney.currentJourneyStep.code]);

  return (
    <JourneyContext.Provider
      value={{
        currentMap,
        isJourneyLoading: !journeyEverLoaded.current,
        // Internal `journeyMapView` state: true (3D/2D toggle on), false
        // (shortcuts), or "errored" (3D/WebGL failed — force shortcuts, hide
        // toggle).
        journeyMapErrored: journeyMapView === JOURNEY_MAP_VIEW_ERRORED,
        journeyMapView: journeyMapView === true,
        nextMapUnlocked: unlockedMaps[currentMap + 1],
        requestMapChange,
        requestStepChange,
        setJourneyMapView,
        unlockedSteps,
      }}
    >
      {children}
    </JourneyContext.Provider>
  );
};

JourneyProvider.propTypes = {
  children: PropTypes.node,
};
